import { NotificationStoreInstance } from '@common/stores';
import { IAppointmentTypeModel, ClientConfigurationModel } from '@common/models';
import { computed, flow, makeObservable, observable } from "mobx";
import { AppointmentTypeService } from '@common/services/appointmentTypeService';
import { petcoPhilippineBronze } from '@common/_assets/color-constants.js';
import { IQueryCacheStoreInit, QueryCacheStore } from '@common/stores/QueryCacheStore';
import { SearchService } from '@common/services';

const defaultAppointmentType: IAppointmentTypeModel = {
    authApplicationId: 2, // SWIFT
    appointmentTypeId: 0,
    name: "New Appointment Type",
    description: "",
    color: petcoPhilippineBronze,
    defaultDurationSeconds: 1800,
    species: [{ speciesId: 1, name: "Canine", rowVersion: "" }, { speciesId: 2, name: "Feline", rowVersion: "" }],
    isActive: false,
    onlineBooking: false,
    isDropoff: false,
    isSurgery: false,
    rowVersion: "",
    isLocked: false,
    isWaitListed: false,
    isProactivePrescribing: false,
    requiresPostAnesthesiaFollowup: false
}

export interface IAppointmentTypesStoreInit extends IQueryCacheStoreInit {
    configuration: ClientConfigurationModel;
}

export class AppointmentTypesStore extends QueryCacheStore {
    configuration: ClientConfigurationModel;
    @observable loading: boolean = false;
    @observable redirectToUrl: string | undefined = undefined;
    @observable appointmentType: IAppointmentTypeModel = defaultAppointmentType;

    constructor(init: IAppointmentTypesStoreInit) {
        super(init)

        this.configuration = init.configuration;

        makeObservable(this, undefined, {
            autoBind: true
        }); //use decorators
    }

    @computed get species() {
        return this.configuration.species;
    }

    refreshAppointmentType = flow(function* (this: AppointmentTypesStore, appointmentTypeId?: string) {
        if (appointmentTypeId === undefined) return;
        const id = parseInt(appointmentTypeId);
        if (isNaN(id)) return;

        this.loading = true;

        this.appointmentType = id === 0
            ? defaultAppointmentType
            : yield AppointmentTypeService.getAppointmentType(appointmentTypeId)
                .catch((error) => NotificationStoreInstance.onError(`Failed to fetch Appointment Type. ${error.message}`, defaultAppointmentType));

        this.loading = false;
    });

    /**
     * Adds or update an appointment type
     * @param this current AppointmentTypesStore reference
     * @param appointmentTypeToAdd appointment type model to create
     */
    upsertAppointmentType = flow(function* (this: AppointmentTypesStore, appointmentType: IAppointmentTypeModel) {
        this.loading = true;

        this.appointmentType = appointmentType.appointmentTypeId === 0
            ? yield AppointmentTypeService.addAppointmentType(appointmentType)
                .then(NotificationStoreInstance.onSuccess("Appointment type added"))
                .catch(NotificationStoreInstance.onError("Failed to add appointment type", appointmentType))
            : yield AppointmentTypeService.updateAppointmentType(appointmentType)
                .then(NotificationStoreInstance.onSuccess("Appointment type updated"))
                .catch(NotificationStoreInstance.onError("Failed to update appointment type", appointmentType));

        this.clearCache({ queryKey: [SearchService.SearchEndPoints.appointmentTypes] })
        this.loading = false;
    });
}
